import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Zap } from "lucide-react";
import { api } from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { toast } from "sonner";
import { useShorthandExpansion } from "../hooks/useShorthandExpansion";

export default function QuickCapture() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const { onKeyDown: shorthandKeyDown } = useShorthandExpansion();
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const { current } = useWorkspace();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === " ") {
        e.preventDefault();
        setOpen(v => !v);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    // Also listen for custom event from sidebar/calendar
    const onEvent = () => setOpen(true);
    window.addEventListener("crescendo:quick-capture", onEvent);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("crescendo:quick-capture", onEvent); };
  }, []);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 50); }, [open]);

  const save = async (asScheduled = false) => {
    if (!text.trim() || !current?.workspace_id) return;
    setSaving(true);
    try {
      // Use AI to detect if there's a time/date — if not, save as unscheduled
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const { data } = await api.post(`/workspaces/${current.workspace_id}/events/from-text`, {
        text: text.trim(), client_tz: tz, client_now_iso: new Date().toISOString(),
        force_unscheduled: !asScheduled,
      });
      toast.success(`"${data.title}" added`);
      setText("");
      setOpen(false);
      window.dispatchEvent(new CustomEvent("crescendo:events-changed"));
    } catch {
      // Fallback: create plain unscheduled task
      try {
        await api.post(`/workspaces/${current.workspace_id}/events`, {
          title: text.trim(), type: "task", unscheduled: true,
          start: new Date().toISOString(), end: new Date(Date.now() + 3600000).toISOString(),
        });
        toast.success("Added to unscheduled");
        setText(""); setOpen(false);
        window.dispatchEvent(new CustomEvent("crescendo:events-changed"));
      } catch { toast.error("Failed to capture"); }
    }
    setSaving(false);
  };

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-32" onClick={() => setOpen(false)}>
      <div onClick={e => e.stopPropagation()}
        className="bg-[#141414] border border-[#2A2A2A] rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden"
        style={{ backdropFilter: "blur(20px)" }}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#2A2A2A]">
          <Zap className="w-4 h-4 text-[#5A90F5] flex-shrink-0" />
          <span className="text-[11px] text-[#A1A1AA] font-medium">Quick Capture</span>
          <span className="text-[10px] text-[#3F3F46] ml-auto">⌘ Space</span>
          <button onClick={() => setOpen(false)} className="text-[#52525B] hover:text-[#A1A1AA]"><X className="w-3.5 h-3.5" /></button>
        </div>
        <textarea
          ref={inputRef}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => {
            shorthandKeyDown(e, text, setText);
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); save(false); }
            if (e.key === "Enter" && e.shiftKey) { e.preventDefault(); save(true); }
          }}
          placeholder="Capture a task, idea, or note… AI will detect dates/times"
          className="w-full bg-transparent px-4 py-3 text-sm text-[#EDEDED] outline-none resize-none placeholder:text-[#3F3F46]"
          rows={3}
        />
        <div className="flex items-center justify-between px-4 py-2 border-t border-[#1a1a1a]">
          <span className="text-[10px] text-[#3F3F46]">↵ Unscheduled · ⇧↵ AI-schedule</span>
          <div className="flex gap-2">
            <button onClick={() => save(false)} disabled={!text.trim() || saving}
              className="text-[11px] px-3 py-1.5 rounded border border-[#2A2A2A] text-[#A1A1AA] hover:text-[#EDEDED] hover:bg-[#1a1a1a] disabled:opacity-40">
              Unscheduled
            </button>
            <button onClick={() => save(true)} disabled={!text.trim() || saving}
              className="text-[11px] px-3 py-1.5 rounded bg-[#5A90F5] text-white font-medium disabled:opacity-40">
              {saving ? "…" : "Add"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
